/**
 * Client side of a session (ADR-015 §6, §10). One instance per app and tab:
 * - the access token lives in memory only; the refresh token is an httpOnly cookie the API scopes
 *   per app (`desp_rt_admin` / `desp_rt_driver`, path /auth);
 * - the token is refreshed before it expires (~80 % of its life) and only one refresh runs at a time
 *   across the tabs of the browser (`navigator.locks`); the result reaches the other tabs through a
 *   `BroadcastChannel`, which also carries sign-in and sign-out;
 * - a 429, a 5xx or a network failure never ends the session: only the API saying the session is
 *   gone does (401 SESSION_EXPIRED / SESSION_REVOKED from the refresh or any authenticated route);
 * - signing out always clears the local session, even if the server cannot be reached.
 */
import type { TenantAudience } from '@despachador/domain';

export type AppName = TenantAudience;

/** Why there is no session: shown as a status screen (none = never signed in, or signed out on purpose). */
export type SignedOutReason = 'none' | 'expired' | 'revoked' | 'suspended' | 'disabled' | 'tenant_suspended' | 'unavailable';

export type SessionState =
  | { status: 'checking' }
  /** `epoch` changes when a different session starts (a sign-in), not when the token is refreshed. */
  | { status: 'signed-in'; epoch: number }
  | { status: 'signed-out'; reason: SignedOutReason };

/** ok: there is a fresh token · dead: the session is over (state already signed-out) · unavailable: try again later. */
export type RefreshOutcome = 'ok' | 'dead' | 'unavailable';

export interface AccessGrant {
  accessToken: string;
  /** Seconds. */
  expiresIn: number;
}

/** Error of the refresh or logout endpoints, as the API answers it. */
export interface SessionFailure {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

type Message =
  | { type: 'refreshed'; accessToken: string; expiresAt: number }
  | { type: 'signed-in'; accessToken: string; expiresAt: number }
  | { type: 'signed-out'; reason: SignedOutReason };

/** Refresh at this share of the token's life. */
const REFRESH_AT = 0.8;
/** Retry a refresh that could not reach the API (network, 429, 5xx) after this long, doubling up to the max. */
const RETRY_MIN_MS = 5_000;
const RETRY_MAX_MS = 60_000;
/** The logout request gets this long; the local session is cleared anyway. */
const LOGOUT_TIMEOUT_MS = 5_000;

/** Headers every request of the apps carries: the CSRF guard of the cookie endpoints and the app. */
export function appHeaders(app: AppName): Record<string, string> {
  return { 'X-Requested-With': 'despachador', 'X-App': app };
}

/** Stable key of this browser for the session's device binding (refresh grace, session list). */
export function deviceKey(): string {
  try {
    let k = localStorage.getItem('desp.device');
    if (!k) {
      // randomUUID only exists in secure contexts; the Android shell loads the dev app over LAN http
      k = crypto.randomUUID?.() ?? Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem('desp.device', k);
    }
    return k;
  } catch {
    return 'ephemeral';
  }
}

/** Maps what the API said about a dead session to the reason shown to the person. */
export function signedOutReason(code: string | undefined, details: unknown, phase: 'start' | 'running'): SignedOutReason {
  if (code === 'SESSION_REVOKED') {
    const why = (details as { reason?: string } | undefined)?.reason;
    if (why === 'USER_SUSPENDED') return 'suspended';
    if (why === 'USER_DISABLED') return 'disabled';
    if (why === 'TENANT_SUSPENDED') return 'tenant_suspended';
    return 'revoked';
  }
  // at start-up a missing or expired cookie is just "not signed in": a first visit looks the same
  return phase === 'start' ? 'none' : 'expired';
}

export class SessionManager {
  private accessToken: string | null = null;
  private expiresAt = 0;
  private lifetimeMs = 0;
  /** Increases with every token this tab adopts (own refresh or another tab's). */
  private grants = 0;
  private epoch = 0;
  private current: SessionState = { status: 'checking' };
  private readonly listeners = new Set<(s: SessionState) => void>();
  private inFlight: Promise<RefreshOutcome> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retryMs = RETRY_MIN_MS;
  private readonly channel: BroadcastChannel | null;
  /** Why the last refresh could not complete (network, 429, 5xx), for the caller that needed it. */
  lastFailure: SessionFailure | null = null;

  constructor(
    readonly baseUrl: string,
    readonly app: AppName,
  ) {
    this.channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(`despachador.session.${app}`);
    this.channel?.addEventListener('message', (e: MessageEvent<Message>) => this.receive(e.data));
    if (typeof document !== 'undefined')
      document.addEventListener('visibilitychange', () => {
        // background tabs throttle timers: catch up when the tab is shown again
        if (document.visibilityState === 'visible' && this.current.status === 'signed-in' && Date.now() >= this.dueAt()) void this.refresh();
      });
  }

  // ------------------------------------------------------------------ state
  get state(): SessionState {
    return this.current;
  }

  /** The access token, or null. Never stored anywhere but this object. */
  get token(): string | null {
    return this.accessToken;
  }

  /** Every change of state reaches every listener. Returns the unsubscribe function. */
  subscribe(fn: (s: SessionState) => void): () => void {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }

  private set(next: SessionState) {
    this.current = next;
    this.listeners.forEach((l) => l(next));
  }

  /** Headers of this app (CSRF guard, app, device). */
  headers(): Record<string, string> {
    return { ...appHeaders(this.app), 'X-Device-Key': deviceKey() };
  }

  // ------------------------------------------------------------------ lifecycle
  /** Start-up (or "Reintentar"): is there a session behind the cookie? */
  async start(): Promise<void> {
    if (this.current.status !== 'checking') this.set({ status: 'checking' });
    const outcome = await this.refresh('start');
    if (outcome === 'unavailable' && this.current.status === 'checking') this.set({ status: 'signed-out', reason: 'unavailable' });
  }

  /** A sign-in finished (password, second factor): this is the new session of every tab. */
  establish(grant: AccessGrant): void {
    this.adopt(grant.accessToken, Date.now() + grant.expiresIn * 1000);
    this.epoch++;
    this.set({ status: 'signed-in', epoch: this.epoch });
    this.post({ type: 'signed-in', accessToken: grant.accessToken, expiresAt: this.expiresAt });
  }

  /** The API said the session is over (401 SESSION_EXPIRED / SESSION_REVOKED). */
  end(reason: SignedOutReason, broadcast = true): void {
    this.clear();
    if (this.current.status === 'signed-out' && this.current.reason === reason) return;
    this.set({ status: 'signed-out', reason });
    if (broadcast) this.post({ type: 'signed-out', reason });
  }

  /**
   * Signs out: the server revokes the session named by the cookie (no bearer needed, so an expired
   * access token does not matter) and the local session is cleared whatever the server answers.
   */
  async signOut(): Promise<void> {
    this.stopTimer();
    try {
      await this.exclusive(async () => {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), LOGOUT_TIMEOUT_MS);
        try {
          await fetch(`${this.baseUrl}/auth/logout`, { method: 'POST', credentials: 'include', headers: { ...this.headers(), 'Content-Type': 'application/json' }, body: '{}', signal: ctl.signal });
        } finally {
          clearTimeout(t);
        }
      });
    } catch {
      // offline or server error: this browser still forgets the session
    } finally {
      this.end('none');
    }
  }

  /** A token that is valid now, refreshing first when it is missing or about to expire. null = no session. */
  async freshToken(): Promise<string | null> {
    if (this.current.status === 'signed-out') return null;
    if (this.accessToken && Date.now() < this.expiresAt - 5_000) return this.accessToken;
    return (await this.refresh()) === 'ok' ? this.accessToken : null;
  }

  /**
   * Gets a new access token with the refresh cookie. One at a time in the tab and across tabs: a tab
   * that waited for another one's refresh takes that token instead of rotating the cookie again.
   */
  refresh(phase: 'start' | 'running' = 'running'): Promise<RefreshOutcome> {
    this.inFlight ??= (async () => {
      const seen = this.grants;
      try {
        return await this.exclusive(async () => {
          if (this.grants !== seen && this.accessToken) return 'ok' as const;
          return this.refreshNow(phase);
        });
      } catch {
        // the lock could not be taken (refreshNow itself never throws)
        return this.unavailable({ status: 0, code: 'NETWORK_ERROR', message: 'No se pudo renovar la sesión' });
      } finally {
        setTimeout(() => (this.inFlight = null), 0);
      }
    })();
    return this.inFlight;
  }

  private async refreshNow(phase: 'start' | 'running'): Promise<RefreshOutcome> {
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}/auth/refresh`, { method: 'POST', credentials: 'include', headers: { ...this.headers(), 'Content-Type': 'application/json' }, body: '{}' });
    } catch {
      return this.unavailable({ status: 0, code: 'NETWORK_ERROR', message: 'No hay conexión con el servidor' });
    }
    if (res.ok) {
      const g = (await res.json().catch(() => null)) as AccessGrant | null;
      if (!g?.accessToken) return this.unavailable({ status: res.status, code: 'BAD_RESPONSE', message: 'Respuesta inesperada del servidor' });
      const wasSignedIn = this.current.status === 'signed-in';
      this.adopt(g.accessToken, Date.now() + g.expiresIn * 1000);
      this.lastFailure = null;
      this.retryMs = RETRY_MIN_MS;
      if (!wasSignedIn) {
        this.epoch++;
        this.set({ status: 'signed-in', epoch: this.epoch });
      }
      this.post({ type: 'refreshed', accessToken: g.accessToken, expiresAt: this.expiresAt });
      return 'ok';
    }
    const body = (await res.json().catch(() => ({}))) as { code?: string; message?: string; details?: unknown };
    if (res.status === 401) {
      this.end(signedOutReason(body.code, body.details, phase));
      return 'dead';
    }
    // 429, 5xx, a misconfigured proxy: the session may well be alive, try again later
    return this.unavailable({ status: res.status, code: body.code ?? 'ERROR', message: body.message ?? res.statusText, details: body.details });
  }

  private unavailable(f: SessionFailure): RefreshOutcome {
    this.lastFailure = f;
    if (this.current.status === 'signed-in') {
      this.schedule(Date.now() + this.retryMs);
      this.retryMs = Math.min(RETRY_MAX_MS, this.retryMs * 2);
    }
    return 'unavailable';
  }

  // ------------------------------------------------------------------ tokens and timers
  private adopt(accessToken: string, expiresAt: number) {
    this.accessToken = accessToken;
    this.lifetimeMs = Math.max(1_000, expiresAt - Date.now());
    this.expiresAt = expiresAt;
    this.grants++;
    this.schedule(this.dueAt());
  }

  private dueAt(): number {
    return this.expiresAt - this.lifetimeMs * (1 - REFRESH_AT);
  }

  private schedule(at: number) {
    this.stopTimer();
    this.timer = setTimeout(() => void this.refresh(), Math.max(0, at - Date.now()));
  }

  private stopTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private clear() {
    this.stopTimer();
    this.accessToken = null;
    this.expiresAt = 0;
  }

  /** Runs `fn` holding the browser-wide lock of this app's refresh cookie (when the browser has one). */
  private exclusive<T>(fn: () => Promise<T>): Promise<T> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    return locks ? (locks.request(`despachador.refresh.${this.app}`, fn) as Promise<T>) : fn();
  }

  // ------------------------------------------------------------------ other tabs
  private post(m: Message) {
    try {
      this.channel?.postMessage(m);
    } catch {
      // channel closed: nothing to tell
    }
  }

  private receive(m: Message) {
    if (!m || typeof m !== 'object') return;
    if (m.type === 'signed-out') {
      if (this.current.status !== 'signed-out') this.end(m.reason, false);
      return;
    }
    if (m.expiresAt <= Date.now()) return;
    const newSession = m.type === 'signed-in' || this.current.status !== 'signed-in';
    this.adopt(m.accessToken, m.expiresAt);
    this.lastFailure = null;
    if (newSession) {
      this.epoch++;
      this.set({ status: 'signed-in', epoch: this.epoch });
    }
  }
}
